// @flow
import React, { memo } from 'react';
import classnames from 'classnames';

import ratioUtils from 'Components/utils/ratios/ratios.utils';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';

import DynamicLink, {
  type Props as DynamicLinkProps,
} from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import Image from 'Components/frameworks/image/Image.jsx';
import { ImageSlideShow } from 'Components/frameworks/image-slideshow/ImageSlideShow.jsx';
import { constants } from '@clubmed/utils';

const { OPENING_STATUS } = constants;

export type ProductThumbnailProps = {
  closedLabel?: string,
  className?: string,
  hasBorder?: boolean,
  hasLazyLoading?: boolean,
  hasTransport?: boolean,
  isLinkDisabled?: boolean,
  isSliderDisabled?: boolean,
  link?: DynamicLinkProps,
  nextLabel?: string,
  openingSoonLabel?: string,
  pictures: Array<{
    alt?: string,
    src: string,
  }>,
  openingStatus?: string,
  previousLabel?: string,
  productTags?: ?string,
  tagsColor?: string,
  transportLabel?: string,
};

export const ProductThumbnail = memo<ProductThumbnailProps>(function ProductThumbnail(
  props: ProductThumbnailProps,
) {
  const {
    closedLabel,
    className,
    hasBorder,
    hasTransport,
    isLinkDisabled,
    link,
    nextLabel,
    openingStatus,
    openingSoonLabel,
    pictures = [],
    previousLabel,
    productTags,
    tagsColor = COLORS.BLUE,
    transportLabel,
    isSliderDisabled,
    hasLazyLoading,
  } = props;

  const TagName = isLinkDisabled ? 'div' : DynamicLink;

  const defaultPicture = pictures[0] || {};

  return (
    <TagName
      className={classnames('block', 'image-responsive-cell', 'relative', className)}
      {...(isLinkDisabled ? null : link)}
    >
      {!isSliderDisabled && pictures.length > 1 && openingStatus !== OPENING_STATUS.CLOSED ? (
        <ImageSlideShow
          hasInsetBorder={hasBorder}
          hasLazyLoading
          media={pictures}
          nextLabel={nextLabel}
          previousLabel={previousLabel}
          ratio={ratioUtils.XL}
        />
      ) : (
        <Image
          altMsg={defaultPicture.alt}
          className={classnames({
            'inset-border-after': hasBorder,
          })}
          imageUrl={defaultPicture.src}
          key={defaultPicture.src}
          groupKey="thumbnails"
          ratioKey={ratioUtils.XL}
          hasLazyLoading={hasLazyLoading}
        />
      )}
      {hasTransport && (
        <div
          className="bg-blue inline-flex items-center overflow-hidden p-2 text-xs text-white uppercase absolute top-0 rtl:right-0 mt-5 rtl:mr-5 rounded-tiny font-brand uppercase"
          data-testid="ProductThumbnail-transport"
        >
          <Icon
            className="flex-shrink-0 rtl:mr-1"
            name={ICONS.TRANSPORT}
            width="calc(10rem / 16)"
          />
          <div className="leading-tight text-xxs" data-testid="ProductThumbnail-transportLabel">
            {transportLabel}
          </div>
        </div>
      )}
      {openingStatus === OPENING_STATUS.OPENING_SOON && (
        <div
          className="bg-orange inline-flex items-center overflow-hidden p-2 text-xs text-white uppercase absolute top-0 rtl:right-0 mt-5 rtl:mr-5 rounded-tiny font-bold"
          data-testid="ProductThumbnail-openingSoon"
        >
          <div className="leading-tight text-xxs" data-testid="ProductThumbnail-openingSoonlabel">
            {openingSoonLabel}
          </div>
        </div>
      )}
      {openingStatus === OPENING_STATUS.CLOSED && (
        <div
          className="absolute bg-overlay-black-60 font-bold inline-flex items-center justify-center overflow-hidden inset-0 rounded-tiny text-white text-xl uppercase"
          data-testid="ProductThumbnail-closed"
        >
          <div data-testid="ProductThumbnail-closedLabel">{closedLabel}</div>
        </div>
      )}
      {!!productTags && (
        <div
          data-testid="ProductThumbnail-bottomTags"
          className={classnames(
            `bg-${tagsColor}`,
            'absolute leading-one font-brand uppercase left-1/2 bottom-0 p-2 mb-5 rounded-tiny text-center text-white text-xs -translate-x-50 whitespace-no-wrap max-w-9/10 truncate z-1',
          )}
        >
          {productTags}
        </div>
      )}
    </TagName>
  );
});
